import React, { useState, useEffect, useRef } from 'react';
import { useForm, SubmitHandler } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import Modal from '../Common/Modal';
import Input from '../Forms/Input';
import Select from '../Forms/Select';
import { Button } from '../Common/button';
import { Interview, InterviewUpdate, InterviewAnswerUpdate, InterviewAnswer } from '../../types';

interface InterviewMetadataForm {
    name: string | null;
    status: string | null;
    interview_date: string | null;
    interviewee_name: string | null;
    general_notes: string | null;
}

interface EditableAnswerState {
    id: string;
    question_order: number | string;
    answer_text: string | null;
}

interface InterviewEditModalProps {
  isOpen: boolean;
  onClose: () => void;
  interview: Interview | null;
  onSave: (interviewId: string, data: InterviewUpdate) => Promise<void>;
  isLoading: boolean;
  isViewOnly?: boolean;
}

const formatDateForInput = (date: string | Date | null | undefined): string => {
  if (!date) return '';
  try {
    if (typeof date === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(date)) return date;
    const d = new Date(date);
    if (isNaN(d.getTime())) return '';
    const year = d.getFullYear();
    const month = (d.getMonth() + 1).toString().padStart(2, '0');
    const day = d.getDate().toString().padStart(2, '0');
    return `${year}-${month}-${day}`;
  } catch { return ''; }
};

const formatDateDisplay = (date: string | Date | null | undefined, locale: string): string => {
  if (!date) return '—';
  try {
    return new Date(date).toLocaleDateString(locale, { year: 'numeric', month: 'long', day: 'numeric' });
  } catch { return '—'; }
};

const ViewField: React.FC<{ label: string; value?: string | null; children?: React.ReactNode }> = ({ label, value, children }) => (
  <div>
    <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-1">{label}</p>
    {children ?? <p className="text-sm text-foreground">{value || '—'}</p>}
  </div>
);

export const InterviewEditModal: React.FC<InterviewEditModalProps> = ({
  isOpen,
  onClose,
  interview,
  onSave,
  isLoading,
  isViewOnly = false,
}) => {
  const { t, i18n } = useTranslation();
  const formRef = useRef<HTMLFormElement>(null);
  const { register, handleSubmit, reset, formState: { errors, isDirty: isMetadataDirty } } = useForm<InterviewMetadataForm>();
  const [currentAnswers, setCurrentAnswers] = useState<EditableAnswerState[]>([]);
  const [isAnswerDirty, setIsAnswerDirty] = useState<boolean>(false);

  useEffect(() => {
    if (interview && isOpen) {
      reset({
        name: interview.name,
        status: interview.status,
        interview_date: formatDateForInput(interview.interview_date),
        interviewee_name: interview.interviewee_name,
        general_notes: interview.general_notes,
      });

      const initialAnswers = interview.answers.map((ans: InterviewAnswer) => ({
        id: ans.id,
        question_order: ans.question_order,
        answer_text: ans.answer_text,
      })).sort((a: EditableAnswerState, b: EditableAnswerState) =>
        Number(a.question_order) - Number(b.question_order)
      );

      setCurrentAnswers(initialAnswers);
      setIsAnswerDirty(false);
    }
  }, [interview, isOpen, reset]);

  const handleAnswerChange = (answerId: string, text: string | null) => {
    setCurrentAnswers(prev =>
      prev.map(ans =>
        ans.id === answerId ? { ...ans, answer_text: text } : ans
      )
    );
    if (!isAnswerDirty) setIsAnswerDirty(true);
  };

  const processSubmit: SubmitHandler<InterviewMetadataForm> = async (metadata) => {
    if (!interview) return;

    console.log('=== SUBMIT DEBUG ===');
    console.log('currentAnswers state:', currentAnswers);
    console.log('interview.answers:', interview.answers);

    const updatedAnswersPayload: InterviewAnswerUpdate[] = currentAnswers.map(ans => {
      const orderNum = typeof ans.question_order === 'string'
        ? parseInt(ans.question_order, 10)
        : ans.question_order;

      console.log(`Converting answer ${ans.id}: question_order ${ans.question_order} (${typeof ans.question_order}) -> ${orderNum} (${typeof orderNum})`);

      return {
        id: ans.id,
        question_order: orderNum,
        answer_text: ans.answer_text,
      };
    }).filter(ans => !isNaN(ans.question_order));

    console.log('Payload before filter:', currentAnswers.map(ans => ({ id: ans.id, q: ans.question_order, a: ans.answer_text })));
    console.log('Payload after filter:', updatedAnswersPayload);

    const payload: InterviewUpdate = {
      name: metadata.name,
      status: metadata.status,
      interview_date: metadata.interview_date || null,
      interviewee_name: metadata.interviewee_name,
      general_notes: metadata.general_notes,
      answers: updatedAnswersPayload,
    };

    console.log('Final payload:', JSON.stringify(payload, null, 2));

    try {
        await onSave(interview.id, payload);
    } catch (error) {
        console.error("Error saving interview from modal:", error);
    }
  };

  const handleSaveClick = () => {
    if (formRef.current) {
      formRef.current.requestSubmit();
    }
  };

  const canSave = isMetadataDirty || isAnswerDirty;

  if (!interview) return null;

  const statusLabel = interview.status === 'Completed'
    ? t('interview.edit.statusCompleted')
    : interview.status === 'In Progress'
    ? t('interview.edit.statusInProgress')
    : t('interview.edit.statusPlanned');

  const statusColor = interview.status === 'Completed'
    ? 'bg-green-100 text-green-800'
    : interview.status === 'In Progress'
    ? 'bg-blue-100 text-blue-800'
    : 'bg-gray-100 text-gray-700';

  const sortedAnswers = interview.answers
    .slice()
    .sort((a: InterviewAnswer, b: InterviewAnswer) => Number(a.question_order) - Number(b.question_order));

  return (
    <Modal
      title={`${isViewOnly ? t('interview.edit.viewTitle', 'Entretien') : t('interview.edit.editTitle')}: ${interview.name || interview.id.substring(0, 8)}...`}
      open={isOpen}
      onClose={onClose}
      className="w-full max-w-[min(95vw,72rem)]"
      hideDefaultFooter={true}
    >
      {isViewOnly ? (
        /* ── View mode: plain text display ── */
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">

          {/* Left column – General info */}
          <div className="space-y-5">
            <h4 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              {t('interview.edit.generalInfo')}
            </h4>
            <ViewField label={t('interview.edit.name')} value={interview.name} />
            <ViewField label={t('interview.edit.status')}>
              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${statusColor}`}>
                {statusLabel}
              </span>
            </ViewField>
            <ViewField label={t('interview.edit.date')} value={formatDateDisplay(interview.interview_date, i18n.language)} />
            <ViewField label={t('interview.edit.intervieweeName')} value={interview.interviewee_name} />
            <ViewField label={t('interview.edit.generalNotes')}>
              {interview.general_notes
                ? <p className="text-sm text-foreground whitespace-pre-wrap">{interview.general_notes}</p>
                : <p className="text-sm text-muted-foreground">—</p>
              }
            </ViewField>
          </div>

          {/* Right column – Questions & Answers */}
          <div>
            <h4 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-4">
              {t('interview.edit.questionsAnswers')}
            </h4>
            <div className="space-y-4 overflow-y-auto max-h-[60vh] pr-2">
              {sortedAnswers.map((answer: InterviewAnswer) => (
                <div key={answer.id} className="p-3 border border-border rounded-md space-y-1">
                  <p className="text-sm font-semibold text-foreground">
                    Q{answer.question_order}: {answer.question_text}
                  </p>
                  {answer.answer_text
                    ? <p className="text-sm text-foreground whitespace-pre-wrap">{answer.answer_text}</p>
                    : <p className="text-sm text-muted-foreground italic">{t('interview.edit.noAnswer', 'Aucune réponse')}</p>
                  }
                </div>
              ))}
              {interview.answers.length === 0 && (
                <p className="text-muted-foreground italic p-3">
                  {t('interview.edit.noQuestions')}
                </p>
              )}
            </div>
          </div>

        </div>
      ) : (
        /* ── Edit mode: form inputs ── */
        <form ref={formRef} onSubmit={handleSubmit(processSubmit)}>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">

            {/* Left column – General info */}
            <div className="space-y-4">
              <h4 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                {t('interview.edit.generalInfo')}
              </h4>
              <Input
                label={t('interview.edit.name')}
                name="name"
                register={register}
                error={errors.name}
              />
              <Select
                label={t('interview.edit.status')}
                name="status"
                register={register}
                options={[
                  { value: 'Planned', label: t('interview.edit.statusPlanned') },
                  { value: 'In Progress', label: t('interview.edit.statusInProgress') },
                  { value: 'Completed', label: t('interview.edit.statusCompleted') },
                ]}
                error={errors.status}
              />
              <Input
                label={t('interview.edit.date')}
                name="interview_date"
                type="date"
                register={register}
                error={errors.interview_date}
              />
              <Input
                label={t('interview.edit.intervieweeName')}
                name="interviewee_name"
                register={register}
                error={errors.interviewee_name}
              />
              <div>
                <label htmlFor="general_notes" className="block text-sm font-medium text-foreground mb-1">
                  {t('interview.edit.generalNotes')}
                </label>
                <textarea
                  id="general_notes"
                  rows={6}
                  className="w-full px-3 py-2 border border-border rounded-md shadow-sm bg-background text-foreground focus:outline-none focus:ring-1 focus:ring-ring focus:border-ring sm:text-sm"
                  {...register('general_notes')}
                />
              </div>
            </div>

            {/* Right column – Questions & Answers (scrollable) */}
            <div>
              <h4 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-4">
                {t('interview.edit.questionsAnswers')}
              </h4>
              <div className="space-y-4 overflow-y-auto max-h-[60vh] pr-2">
                {sortedAnswers.map((answer: InterviewAnswer) => {
                  const currentAnswer = currentAnswers.find(ca => ca.id === answer.id);
                  return (
                    <div key={answer.id} className="p-3 border border-border rounded-md">
                      <p className="text-sm font-semibold text-foreground mb-2">
                        Q{answer.question_order}: {answer.question_text}
                      </p>
                      <textarea
                        rows={3}
                        value={currentAnswer?.answer_text || ''}
                        onChange={(e) => handleAnswerChange(answer.id, e.target.value)}
                        placeholder={t('interview.edit.answerPlaceholder')}
                        className="block w-full border border-border rounded-md shadow-sm p-2 bg-background text-foreground focus:ring-ring focus:border-ring sm:text-sm"
                      />
                    </div>
                  );
                })}
                {interview.answers.length === 0 && (
                  <p className="text-muted-foreground italic p-3">
                    {t('interview.edit.noQuestions')}
                  </p>
                )}
              </div>
            </div>

          </div>
        </form>
      )}

      {/* Footer */}
      <div className="px-4 py-3 border-t border-border flex justify-end gap-3 mt-6">
        <Button onClick={onClose} variant="outline" disabled={isLoading}>
          {t('global.close')}
        </Button>
        {!isViewOnly && (
          <Button onClick={handleSaveClick} disabled={isLoading || !canSave}>
            {isLoading ? t('global.saving') : t('global.save')}
          </Button>
        )}
      </div>
    </Modal>
  );
};
